"use client";

import { Plus } from "lucide-react";
import { useState } from "react";
import { MaskedHeadline, Reveal } from "./motion-primitives";
import { useQuote } from "./quote-context";

const FAQS = [
  {
    q: "How do I request a custom quote?",
    a: "Complete the quote form with the details you currently have. Mills Engraving will review the request and contact you to discuss options, pricing, and next steps.",
  },
  {
    q: "Can I send a logo, artwork, or reference image?",
    a: "Yes. The quote form allows visitors to include files or visual references for review.",
  },
  {
    q: "What products and materials can be engraved?",
    a: "Options depend on the product and project requirements. Submit a quote request so Mills Engraving can confirm what may be suitable.",
  },
  {
    q: "Are there minimum order quantities?",
    a: "Requirements may vary by product and project. Mills Engraving will confirm this during the quote process.",
  },
  {
    q: "How long will my project take?",
    a: "Production timing depends on the product, quantity, artwork, and project requirements. Please include your desired completion date in the quote request.",
  },
  {
    q: "Can you help me find a specific product or brand?",
    a: "Describe what you are looking for and Mills Engraving will help explore the available options.",
  },
  {
    q: "Can I purchase directly from the website?",
    a: "Mills Engraving uses a personalized quote process so product options, engraving details, pricing, and timing can be reviewed before an order begins.",
  },
];

export function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  const { goToQuote } = useQuote();

  return (
    <section className="bg-softwhite py-24 sm:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <p className="eyebrow text-brass-deep">FAQ</p>
          <h2 className="mt-5 font-display text-[clamp(2rem,4.5vw,3.5rem)] leading-[0.98] font-bold text-ink">
            <MaskedHeadline lines={["Frequently asked", "questions."]} />
          </h2>
        </div>

        <div>
          <dl className="border-t border-ink/12">
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.q} className="border-b border-ink/12">
                  <dt>
                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      aria-controls={`faq-panel-${i}`}
                      id={`faq-btn-${i}`}
                      className="flex w-full items-center justify-between gap-6 py-6 text-left"
                    >
                      <span className="font-display text-lg leading-snug font-semibold text-ink sm:text-xl">
                        {f.q}
                      </span>
                      <Plus
                        className={`size-5 shrink-0 text-brass transition-transform duration-400 ${
                          isOpen ? "rotate-45" : ""
                        }`}
                        aria-hidden
                      />
                    </button>
                  </dt>
                  {isOpen && (
                    <dd
                      id={`faq-panel-${i}`}
                      aria-labelledby={`faq-btn-${i}`}
                    >
                      <p className="max-w-2xl pb-7 text-sm leading-relaxed text-muted-foreground">
                        {f.a}
                      </p>
                    </dd>
                  )}
                </div>
              );
            })}
          </dl>

          <Reveal delay={0.1}>
            <p className="mt-10 text-sm text-ink">
              Still have a question?{" "}
              <button
                onClick={() => goToQuote()}
                className="font-semibold text-brass-deep underline underline-offset-4"
              >
                Talk to Mills Engraving.
              </button>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}